
/* ============================================================
   Ecommbase Theme — theme.js
   Vanilla ES module: drawers, cart, gallery, tabs, accordion,
   quick-add, reveal on scroll.
   ============================================================ */
(() => {
  "use strict";

  const T = window.Theme || (window.Theme = {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const on = (el, ev, fn, opt) => el && el.addEventListener(ev, fn, opt);
  const DEBUG =
    typeof window !== "undefined" &&
    /(localhost|127\.0\.0\.1|\.myshopify\.com)/.test(window.location.hostname);
  const warn = (...args) => DEBUG && console.warn(...args);
  const onDelegate = (root, ev, sel, fn) =>
    on(root, ev, (e) => {
      const t = e.target.closest(sel);
      if (t && root.contains(t)) fn(e, t);
    });

  /* ----- Formatters -----
     Soporta los 7 placeholders oficiales de Shopify money_format.
     Ref: https://shopify.dev/docs/api/liquid/filters/money */
  const money = (cents) => {
    const n = (cents || 0) / 100;
    const withThousands = (numStr, sep) => {
      const parts = numStr.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return parts.join(".");
    };
    const dec2 = n.toFixed(2);
    const dec0 = Math.round(n).toString();
    const fmt = T.settings?.money_format || "{{amount}} €";
    return fmt
      // Con decimales
      .replace("{{amount_with_comma_separator}}", withThousands(dec2, ".").replace(/\.(\d{2})$/, ",$1"))
      .replace("{{amount_with_space_separator}}", withThousands(dec2, " ").replace(/\.(\d{2})$/, ",$1"))
      .replace("{{amount_with_apostrophe_separator}}", withThousands(dec2, "'"))
      .replace("{{amount}}", withThousands(dec2, ","))
      // Sin decimales
      .replace("{{amount_no_decimals_with_comma_separator}}", withThousands(dec0, "."))
      .replace("{{amount_no_decimals_with_space_separator}}", withThousands(dec0, " "))
      .replace("{{amount_no_decimals}}", withThousands(dec0, ","));
  };

  /* ----- Storefront VAT (prices arrive NET; markets in ADD_TAXES_AT_CHECKOUT) -----
     Shopify hands every price NET, so we ADD the visitor-country VAT for display, once.
     The rate is the SINGLE source of truth from Liquid (snippets/vat-rate.liquid ->
     window.theme.settings.vat_rate), so JS rounding matches the server-rendered Liquid
     prices and the checkout: round(net * (1 + rate)).
     Default mode "incl" = price WITH VAT (Liquid already rendered it; JS leaves it).
     Mode "excl" = show the stored NET (data-net) — a swap to the exact net, never an
     arithmetic strip, so it can't drift a cent. */
  const VAT_RATE = (T.settings && Number(T.settings.vat_rate)) || 0;
  const VAT_MODE_KEY = "mc_vat_mode";
  /* The header toggle is the B2B app's #mc-vat-toggle, not a [data-vat-toggle] of the
     theme. The app stores its mode in localStorage["mc-vat-mode"] only once the visitor
     touches it; before that its own data-default-mode rules. Same reading as the
     configurators (vatMode() in assets/configurator*.js). */
  const APP_VAT_MODE_KEY = "mc-vat-mode";
  const readAppVatMode = () => {
    try {
      const v = localStorage.getItem(APP_VAT_MODE_KEY);
      if (v === "incl" || v === "excl") return v;
    } catch (e) {}
    const el = document.getElementById("mc-vat-toggle") || document.querySelector("[data-mc-vat-root]");
    const m = el && String(el.getAttribute("data-default-mode") || "").toLowerCase();
    return m === "excl" ? "excl" : "incl";
  };
  let vatMode = "incl";
  const grossCents = (net) =>
    VAT_RATE > 0 ? Math.round(((net || 0) * (100 + VAT_RATE)) / 100) : net || 0;
  const displayCents = (net) => (vatMode === "excl" ? net || 0 : grossCents(net));
  const moneyVat = (net) => money(displayCents(net));
  const vatNote = (net) => {
    if (VAT_RATE <= 0) return "";
    if (vatMode === "excl") {
      const label = (T.strings && T.strings.vat_incl) || "con IVA";
      return `<span class="mc-vat-note">${money(grossCents(net))} ${label}</span>`;
    }
    const label = (T.strings && T.strings.vat_excl) || "sin IVA";
    return `<span class="mc-vat-note">${money(net || 0)} ${label}</span>`;
  };
  /* Elements with data-vat-price belong to the B2B app: it rewrites them itself when its
     toggle changes, starting from the text the theme rendered (with VAT). If the theme also
     swapped them to the net, the app would strip the VAT a second time. Leave them alone
     and keep writing the WITH-VAT amount there. */
  const appOwned = (el) => !!(el && el.hasAttribute("data-vat-price"));
  const moneyFor = (el, net) => (appOwned(el) ? money(grossCents(net)) : moneyVat(net));
  const applyVatMode = (scope) => {
    (scope || document).querySelectorAll("[data-net]:not([data-vat-price])").forEach((el) => {
      const net = parseInt(el.dataset.net, 10);
      if (isNaN(net)) return;
      el.textContent = money(displayCents(net));
    });
  };
  const setVatMode = (mode) => {
    vatMode = mode === "excl" ? "excl" : "incl";
    try { localStorage.setItem(VAT_MODE_KEY, vatMode); } catch (e) {}
    document.documentElement.classList.toggle("vat-excl", vatMode === "excl");
    $$("[data-vat-toggle]").forEach((b) => {
      b.setAttribute("aria-pressed", vatMode === "excl" ? "true" : "false");
      const lbl = $("[data-vat-toggle-label]", b);
      if (lbl)
        lbl.textContent =
          vatMode === "excl"
            ? (T.strings && T.strings.vat_excl) || "sin IVA"
            : (T.strings && T.strings.vat_incl) || "con IVA";
    });
    applyVatMode(document);
    if ($("#CartDrawer")) fetchCart();
  };
  T.vat = { rate: VAT_RATE, grossCents, displayCents, money: moneyVat, apply: applyVatMode, mode: () => vatMode, setMode: setVatMode };
  // The master-custom VAT app supplies a Spanish default in its header switch.
  // Translate only that app label in FR/PT; keep its VAT mode and price logic untouched.
  const initMarketVatSwitchLabels = () => {
    const locale = String(document.documentElement.lang || (window.Shopify && window.Shopify.locale) || "").toLowerCase();
    const labels = /^fr(?:-|$)/.test(locale)
      ? { incl: "TTC", excl: "HT" }
      : /^pt(?:-|$)/.test(locale)
      ? { incl: "IVA incluído", excl: "IVA não incluído" }
      : null;
    if (!labels || !document.body || typeof MutationObserver === "undefined") return;

    const originalVatLabel = /\b(?:IVA|TVA)\s+(incl\.|inclu[ií]d[oa]s?|incluse|excl\.|exclu[ií]d[oa]s?|exclue)(?=$|\s|[)\],;:])/gi;
    const translateLabel = (value) => String(value).replace(originalVatLabel, (match, state) =>
      /^incl/i.test(state) ? labels.incl : labels.excl
    );

    const update = () => {
      const roots = new Set();
      document.querySelectorAll("#mc-vat-toggle, [data-mc-vat-root], .mc-vat-toggle, .mc-vat-toggle__btn, .price-net").forEach((node) => {
        const root = node.closest("[data-mc-vat-root], .mc-vat-toggle")
          || (node.id === "mc-vat-toggle" ? node.parentElement : null)
          || node;
        if (root) roots.add(root);
      });
      roots.forEach((root) => {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let textNode;
        while ((textNode = walker.nextNode())) {
          const translated = translateLabel(textNode.nodeValue);
          if (translated !== textNode.nodeValue) textNode.nodeValue = translated;
        }
        root.querySelectorAll("[aria-label], [title]").forEach((node) => {
          ["aria-label", "title"].forEach((attr) => {
            const original = node.getAttribute(attr);
            const translated = translateLabel(original);
            if (translated !== original) node.setAttribute(attr, translated);
          });
        });
      });
    };

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update();
      });
    });
    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    update();
  };

  const initVatToggle = () => {
    vatMode = readAppVatMode();
    document.documentElement.classList.toggle("vat-excl", vatMode === "excl");
    $$("[data-vat-toggle]").forEach((b) => {
      const lbl = $("[data-vat-toggle-label]", b);
      if (lbl)
        lbl.textContent =
          vatMode === "excl"
            ? (T.strings && T.strings.vat_excl) || "sin IVA"
            : (T.strings && T.strings.vat_incl) || "con IVA";
      b.setAttribute("aria-pressed", vatMode === "excl" ? "true" : "false");
      on(b, "click", (e) => {
        e.preventDefault();
        setVatMode(vatMode === "excl" ? "incl" : "excl");
      });
    });
    if (vatMode === "excl") {
      applyVatMode(document);
      if ($("#CartDrawer")) fetchCart();
    }
    // Follow the app's header toggle (it saves its mode on click) and other tabs.
    const syncFromApp = () => {
      const m = readAppVatMode();
      if (m !== vatMode) setVatMode(m);
    };
    on(document, "click", (e) => {
      if (e.target && e.target.closest && e.target.closest("#mc-vat-toggle, .mc-vat-toggle__btn"))
        setTimeout(syncFromApp, 80);
    });
    on(window, "storage", (e) => {
      if (e.key === APP_VAT_MODE_KEY) syncFromApp();
    });
  };

  /* ----- Toast ----- */
  const toast = (msg, kind = "success") => {
    const root = $("#ToastContainer");
    if (!root) return;
    const el = document.createElement("div");
    el.className = `toast toast--${kind}`;
    el.textContent = msg;
    root.appendChild(el);
    setTimeout(() => el.remove(), 2800);
  };
  T.toast = toast;

  /* ----- Drawer controller ----- */
  const drawerControl = (drawer, trigger) => {
    const open = () => {
      drawer.classList.add("is-open");
      drawer.setAttribute("open", "");
      document.body.classList.add("no-scroll");
      drawer.dispatchEvent(new CustomEvent("drawer:open"));
    };
    const close = () => {
      drawer.classList.remove("is-open");
      drawer.removeAttribute("open");
      document.body.classList.remove("no-scroll");
      drawer.dispatchEvent(new CustomEvent("drawer:close"));
    };
    if (trigger)
      on(trigger, "click", (e) => {
        e.preventDefault();
        open();
      });
    on($(".drawer__overlay", drawer), "click", close);
    $$("[data-drawer-close]", drawer).forEach((b) => on(b, "click", close));
    on(document, "keydown", (e) => {
      if (e.key === "Escape" && drawer.classList.contains("is-open")) close();
    });
    return { open, close };
  };
  T.drawerControl = drawerControl;

  /* ----- Mobile nav drawer ----- */
  const initNavDrawer = () => {
    const drawer = $("#NavDrawer");
    const trigger = $("[data-nav-drawer-open]");
    if (drawer && trigger) drawerControl(drawer, trigger);
    if (!drawer) return;
    const panelFor = (id) => drawer.querySelector(`.m-nav__panel[data-panel="${id}"]`);
    $$("[data-panel-open]", drawer).forEach((btn) => {
      on(btn, "click", (e) => {
        e.preventDefault();
        const panel = panelFor(btn.getAttribute("data-panel-open"));
        if (!panel) return;
        panel.hidden = false;
        requestAnimationFrame(() => panel.classList.add("is-open"));
      });
    });
    $$("[data-panel-close]", drawer).forEach((btn) => {
      on(btn, "click", (e) => {
        e.preventDefault();
        const panel = btn.closest(".m-nav__panel");
        if (!panel) return;
        panel.classList.remove("is-open");
        setTimeout(() => {
          if (!panel.classList.contains("is-open")) panel.hidden = true;
        }, 280);
      });
    });
    on(drawer, "drawer:close", () => {
      $$(".m-nav__panel", drawer).forEach((p) => {
        p.classList.remove("is-open");
        p.hidden = true;
      });
    });
  };

  /* ----- Search drawer ----- */
  const initSearchDrawer = () => {
    const drawer = $("#SearchDrawer");
    const trigger = $("[data-search-drawer-open]");
    if (drawer && trigger) {
      const ctrl = drawerControl(drawer, trigger);
      on(drawer, "drawer:open", () => {
        const i = $("input[name=q]", drawer);
        if (i) setTimeout(() => i.focus(), 150);
      });
    }
  };

  /* ----- Cart drawer ----- */
  const initCartDrawer = () => {
    const drawer = $("#CartDrawer");
    if (!drawer) return;
    const triggers = $$("[data-cart-drawer-open]");
    const ctrl = drawerControl(drawer);
    triggers.forEach((t) =>
      on(t, "click", (e) => {
        if (T.settings?.cart_type !== "drawer") return;
        e.preventDefault();
        fetchCart().then(() => ctrl.open());
      }),
    );
    T.openCart = () => {
      fetchCart().then(() => ctrl.open());
    };
    T.closeCart = ctrl.close;
  };

  /* ----- Cart API ----- */
  const fetchCart = async () => {
    try {
      const res = await fetch(`${T.routes.cart}.js`, {
        headers: { Accept: "application/json" },
      });
      const cart = await res.json();
      renderCart(cart);
      updateCartCount(cart.item_count);
      return cart;
    } catch (e) {
      warn("[cart]", e);
    }
  };
  const cartAdd = async (formData) => {
    const res = await fetch(T.routes.cart_add + ".js", {
      method: "POST",
      body: formData,
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || "Add to cart failed");
    }
    return res.json();
  };
  const cartChange = async (body) => {
    const res = await fetch(T.routes.cart_change + ".js", {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    return res.json();
  };
  T.cart = { fetch: fetchCart, add: cartAdd, change: cartChange };

  const updateCartCount = (count) => {
    $$("[data-cart-count]").forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? "" : "none";
    });
  };

  const renderCart = (cart) => {
    const drawer = $("#CartDrawer");
    if (!drawer) return;
    const itemsBox = $("[data-cart-items]", drawer);
    const footer = $("[data-cart-footer]", drawer);
    const emptyBox = $("[data-cart-empty]", drawer);
    if (!itemsBox) return;

    if (cart.item_count === 0) {
      itemsBox.innerHTML = "";
      if (emptyBox) emptyBox.hidden = false;
      if (footer) footer.hidden = true;
      updateHeadCount(drawer, 0);
      return;
    }
    if (emptyBox) emptyBox.hidden = true;
    if (footer) footer.hidden = false;

    itemsBox.innerHTML = cart.items.map(renderItem).join("");
    updateHeadCount(drawer, cart.item_count);
    updateTotals(drawer, cart);
    updateFreeshipBar(drawer, cart);
    updateShippingLine(drawer, cart);
  };

  const shipFeeFor = (cart) => {
    const fee = (T.settings && T.settings.ship_fee) || 0;
    const bulky = (T.settings && T.settings.ship_fee_bulky) || 0;
    const limit = (T.settings && T.settings.ship_weight_limit) || 0;
    if (limit > 0 && bulky > 0 && (cart.total_weight || 0) > limit) return bulky;
    return fee;
  };

  const updateShippingLine = (drawer, cart) => {
    const el = $("[data-cart-shipping]", drawer);
    if (!el) return;
    const threshold = (T.settings && T.settings.ship_threshold) || 0;
    const fee = shipFeeFor(cart);
    const net = cart.items_subtotal_price;
    if (threshold > 0 && net >= threshold) {
      el.textContent = T.strings?.shipping_free || "Gratis";
      el.style.color = "var(--color-success)";
    } else if (fee > 0) {
      el.innerHTML = moneyVat(fee) + vatNote(fee);
      el.style.color = "";
    } else {
      el.textContent = T.strings?.shipping_calculated || "Se calcula en el pago";
      el.style.color = "var(--color-success)";
    }
  };

  const renderItem = (item) => {
    const compare =
      item.compare_at_price && item.compare_at_price > item.final_price
        ? item.compare_at_price
        : null;
    const img = item.featured_image?.url
      ? `<img src="${item.featured_image.url}&width=120" alt="${item.featured_image.alt || ""}" loading="lazy">`
      : "";
    const variantText =
      item.variant_title && item.variant_title !== "Default Title"
        ? item.variant_title
        : "";
    return `
      <div class="mc-item" data-line-key="${item.key}">
        <div class="mc-item-img">${img}</div>
        <div class="mc-item-info">
          <div class="mc-item-name">${item.product_title}</div>
          ${variantText ? `<div class="mc-item-variant">${variantText}</div>` : ""}
          <div class="mc-item-bottom">
            <div class="mc-qty">
              <button type="button" class="mc-qty-btn" data-qty-dec aria-label="−">−</button>
              <input type="text" inputmode="numeric" pattern="[0-9]*" class="mc-qty-val" value="${item.quantity}" data-qty-input aria-label="Cantidad">
              <button type="button" class="mc-qty-btn" data-qty-inc aria-label="+">+</button>
            </div>
            <div style="display:flex;align-items:center;gap:4px">
              <div class="mc-item-price">
                <span class="mc-price">${moneyVat(item.final_line_price)}</span>
                ${compare ? `<span class="mc-price-old">${moneyVat(compare * item.quantity)}</span>` : ""}
                ${vatNote(item.final_line_price)}
              </div>
              <button type="button" class="mc-item-del" data-cart-remove aria-label="Remove">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>`;
  };

  const updateHeadCount = (drawer, count) => {
    const sub = $("[data-cart-headsub]", drawer);
    if (sub)
      sub.textContent = (T.strings?.items_count || "{{ count }} items").replace(
        "{{ count }}",
        count,
      );
  };

  const updateTotals = (drawer, cart) => {
    const setText = (sel, val) => {
      const el = $(sel, drawer);
      if (el) el.textContent = val;
    };
    const setMoney = (sel, net) => {
      const el = $(sel, drawer);
      if (el) el.innerHTML = moneyVat(net) + vatNote(net);
    };
    setMoney("[data-cart-subtotal]", cart.original_total_price);
    const savings = cart.original_total_price - cart.items_subtotal_price;
    const savingsEl = $("[data-cart-savings]", drawer);
    if (savingsEl) {
      const row = savingsEl.closest(".mc-total-row");
      if (savings > 0) {
        savingsEl.innerHTML = "−" + moneyVat(savings) + vatNote(savings);
        if (row) row.hidden = false;
      } else if (row) {
        row.hidden = true;
      }
    }
    const shipThreshold = (T.settings && T.settings.ship_threshold) || 0;
    const shipFee = shipFeeFor(cart);
    let shipAdd = 0;
    if (shipFee > 0 && !(shipThreshold > 0 && cart.items_subtotal_price >= shipThreshold))
      shipAdd = shipFee;
    setMoney("[data-cart-total]", cart.items_subtotal_price + shipAdd);
  };

  const updateFreeshipBar = (drawer, cart) => {
    const bar = $("[data-freeship-fill]", drawer);
    const txt = $("[data-freeship-txt]", drawer);
    if (!bar || !T.settings?.show_free_shipping_bar) return;
    const threshold =
      (T.settings.ship_threshold || T.settings.free_shipping_threshold) || 0;
    if (threshold <= 0) {
      bar.parentElement.parentElement.hidden = true;
      return;
    }
    const totalG = cart.items_subtotal_price;
    const pct = Math.min(100, (totalG / threshold) * 100);
    bar.style.width = pct + "%";
    if (txt) {
      if (totalG >= threshold) {
        txt.textContent =
          T.strings.free_shipping_reached || "Free shipping unlocked";
      } else {
        const remaining = money(threshold - totalG);
        const tpl =
          T.strings.free_shipping_progress ||
          "Only __AMOUNT__ to free shipping";
        txt.innerHTML = tpl.replace(
          "__AMOUNT__",
          `<strong>${remaining}</strong>`,
        );
      }
    }
  };

  /* ----- Cart drawer interactions (qty, remove) ----- */
  const initCartDrawerEvents = () => {
    const drawer = $("#CartDrawer");
    if (!drawer) return;
    onDelegate(
      drawer,
      "click",
      "[data-qty-inc], [data-qty-dec]",
      async (e, btn) => {
        const row = btn.closest("[data-line-key]");
        if (!row) return;
        const valEl = $(".mc-qty-val", row);
        const current = parseInt(valEl ? valEl.value : "1", 10) || 1;
        const next = btn.hasAttribute("data-qty-inc")
          ? current + 1
          : Math.max(0, current - 1);
        const cart = await cartChange({
          id: row.dataset.lineKey,
          quantity: next,
        });
        renderCart(cart);
      },
    );
    onDelegate(drawer, "change", "[data-qty-input]", async (e, input) => {
      const row = input.closest("[data-line-key]");
      if (!row) return;
      let next = parseInt(input.value, 10);
      if (isNaN(next) || next < 0) next = 1;
      const cart = await cartChange({ id: row.dataset.lineKey, quantity: next });
      renderCart(cart);
    });
    onDelegate(drawer, "keydown", "[data-qty-input]", (e, input) => {
      if (e.key === "Enter") {
        e.preventDefault();
        input.blur();
      }
    });
    onDelegate(drawer, "click", "[data-cart-remove]", async (e, btn) => {
      const row = btn.closest("[data-line-key]");
      const cart = await cartChange({ id: row.dataset.lineKey, quantity: 0 });
      renderCart(cart);
    });
  };

  /* ----- Quick add (product card form) ----- */
  const initQuickAdd = () => {
    onDelegate(document, "submit", "form[data-quick-add]", async (e, form) => {
      e.preventDefault();
      const btn = $("button[type=submit]", form);
      if (btn) {
        btn.disabled = true;
        const span = btn.querySelector("span");
        if (span) span.textContent = T.strings?.adding || "Adding…";
      }
      try {
        const fd = new FormData(form);
        await cartAdd(fd);
        await fetchCart();
        if (T.settings?.cart_type === "drawer") {
          T.openCart?.();
        } else {
          toast(T.strings?.added || "Added");
        }
      } catch (err) {
        toast(err.message || "Error", "error");
      } finally {
        if (btn) {
          btn.disabled = false;
          const span = btn.querySelector("span");
          if (span) span.textContent = T.strings?.add_to_cart || "Add to cart";
        }
      }
    });
  };

  /* ----- Main PDP add to cart ----- */
  const initProductForms = () => {
    onDelegate(
      document,
      "submit",
      "form[data-product-form]",
      async (e, form) => {
        e.preventDefault();
        const btn = $("button[type=submit]", form);
        if (btn) btn.disabled = true;
        try {
          const fd = new FormData(form);
          await cartAdd(fd);
          await fetchCart();
          if (T.settings?.cart_type === "drawer") T.openCart?.();
          else toast(T.strings?.added || "Added");
        } catch (err) {
          toast(err.message || "Error", "error");
        } finally {
          if (btn) btn.disabled = false;
        }
      },
    );
  };

  /* ----- Quantity inputs ----- */
  const initQty = () => {
    onDelegate(
      document,
      "click",
      "[data-qty-input-dec], [data-qty-input-inc]",
      (e, btn) => {
        const wrap = btn.closest(".qty-control");
        const input = $("input", wrap);
        if (!input) return;
        const v = parseInt(input.value, 10) || 1;
        input.value = btn.hasAttribute("data-qty-input-inc")
          ? v + 1
          : Math.max(1, v - 1);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      },
    );
  };

  /* ----- Product gallery ----- */

    const initGallery = () => {
    $$("[data-gallery]").forEach((g) => {
      const main = $("[data-gallery-main]", g);
      const thumbs = $$("[data-gallery-thumb]", g);
      thumbs.forEach((t) =>
        on(t, "click", () => {
          const src = t.dataset.src;
          if (!src || !main) return;

          thumbs.forEach((x) => {
            x.classList.remove("active", "is-active");
            x.setAttribute("aria-pressed", x === t ? "true" : "false");
          });
          t.classList.add("active", "is-active");

          const img = $("img", main);
          const alt = $("img", t)?.alt || "";

          if (img) {
            img.removeAttribute("srcset");
            img.removeAttribute("sizes");
            img.src = src;
            img.alt = alt;
          } else {
            const image = document.createElement("img");
            image.src = src;
            image.alt = alt;
            main.replaceChildren(image);
          }
        }),
      );
    });
  };

  const initThumbsCarousel = () => {
    $$("[data-thumbs-wrap]").forEach((wrap) => {
      const track = $("[data-gallery-thumbs]", wrap);
      const prev = $("[data-thumbs-prev]", wrap);
      const next = $("[data-thumbs-next]", wrap);
      if (!track || !prev || !next) return;
      const update = () => {
        const hasOverflow = track.scrollWidth - track.clientWidth > 1;
        if (!hasOverflow) {
          prev.hidden = true;
          next.hidden = true;
          return;
        }
        prev.hidden = track.scrollLeft <= 1;
        next.hidden =
          track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      };
      const step = () => track.clientWidth * 0.75;
      on(prev, "click", () =>
        track.scrollBy({ left: -step(), behavior: "smooth" }),
      );
      on(next, "click", () =>
        track.scrollBy({ left: step(), behavior: "smooth" }),
      );
      on(track, "scroll", update);
      window.addEventListener("resize", update);
      update();
      setTimeout(update, 200);
    });
  };

  /* ----- Product option picker ----- */
  const initOptionPickers = () => {
    $$("[data-product-form]").forEach((form) => {
      const dataEl = $("[data-pdp-variants]", form);
      if (!dataEl) return;
      let variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      const rows = $$(".config-row", form);
      const variantInput = $("[data-variant-id]", form);
      const submitBtn = form.querySelector(".btn-cart");
      const priceMain = document.querySelector(".price-main");
      const priceOld = document.querySelector(".price-old");
      const priceSave = document.querySelector(".price-save");

      const readSelected = () =>
        rows.map((row) => {
          const active = $(".opt.active", row) || $(".opt", row);
          return active ? active.dataset.value : null;
        });

      const findVariant = (sel) =>
        variants.find(
          (v) =>
            v.options.length === sel.length &&
            v.options.every((o, i) => o === sel[i]),
        );

      const sync = () => {
        const sel = readSelected();
        const match = findVariant(sel);
        if (!match) return;
        if (variantInput) variantInput.value = match.id;
        if (priceMain) {
          priceMain.dataset.net = match.price;
          priceMain.textContent = moneyFor(priceMain, match.price);
        }
        if (priceOld) {
          if (match.compare_at_price && match.compare_at_price > match.price) {
            priceOld.dataset.net = match.compare_at_price;
            priceOld.textContent = moneyFor(priceOld, match.compare_at_price);
            priceOld.style.display = "";
          } else {
            priceOld.style.display = "none";
          }
        }
        if (priceSave) {
          if (match.compare_at_price && match.compare_at_price > match.price) {
            const save = match.compare_at_price - match.price;
            const pct = Math.round((save / match.compare_at_price) * 100);
            priceSave.innerHTML =
              "-" + pct + "% · <span data-net=\"" + save + "\">" + moneyVat(save) + "</span>";
            priceSave.style.display = "";
          } else {
            priceSave.style.display = "none";
          }
        }
        if (submitBtn) {
          submitBtn.disabled = !match.available;
          const label = submitBtn.querySelector("span");
          if (label) {
            label.textContent = match.available
              ? T.strings?.add_to_cart || label.textContent
              : T.strings?.sold_out || label.textContent;
          }
        }
        const envioFr = document.querySelector("[data-envio-fr]");
        if (envioFr) {
          const limit = parseInt(envioFr.dataset.limit, 10) || 0;
          const bulky = (match.weight || 0) > limit;
          const fee = parseInt(bulky ? envioFr.dataset.feeBulky : envioFr.dataset.feeStd, 10) || 0;
          const priceEl = envioFr.querySelector("[data-envio-fr-price]");
          if (priceEl) {
            priceEl.dataset.net = fee;
            priceEl.textContent = moneyVat(fee);
          }
          const htEl = envioFr.querySelector("[data-envio-fr-ht]");
          if (htEl) htEl.textContent = "(" + money(fee) + " HT)";
          const lblEl = envioFr.querySelector("[data-envio-fr-label]");
          if (lblEl) lblEl.textContent = bulky ? envioFr.dataset.lblBulky : envioFr.dataset.lblStd;
        }
        const stickyPrice = document.querySelector("[data-sticky-price]");
        if (stickyPrice) stickyPrice.textContent = moneyVat(match.price);
        const stickyBtn = document.querySelector(".pdp-sticky-atc__btn");
        if (stickyBtn) {
          stickyBtn.disabled = !match.available;
          const sl = stickyBtn.querySelector("span");
          if (sl)
            sl.textContent = match.available
              ? T.strings?.add_to_cart || sl.textContent
              : T.strings?.sold_out || sl.textContent;
        }
      };

      rows.forEach((row) => {
        $$(".opt", row).forEach((btn) =>
          on(btn, "click", () => {
            $$(".opt", row).forEach((b) => b.classList.remove("active"));
            btn.classList.add("active");
            const display = $(".config-label-v", row);
            if (display) display.textContent = btn.dataset.label || btn.textContent.trim();
            sync();
          }),
        );
      });
    });
  };

  /* ----- PDP sticky add-to-cart (mobile) ----- */
  const initPdpStickyAtc = () => {
    const bar = document.querySelector("[data-pdp-sticky]");
    if (!bar) return;
    const anchor = document.querySelector(".main-product .btn-cart");
    if (!anchor || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        const en = entries[0];
        const show = !en.isIntersecting;
        bar.classList.toggle("is-visible", show);
        bar.setAttribute("aria-hidden", show ? "false" : "true");
      },
      { threshold: 0 },
    );
    io.observe(anchor);
  };

  /* ----- Tabs ----- */
  const initTabs = () => {
    $$("[data-tabs]").forEach((root) => {
      const tabs = $$("[role=tab]", root);
      const panels = $$("[role=tabpanel]", root);
      const activate = (idx) => {
        tabs.forEach((t, i) => {
          t.setAttribute("aria-selected", i === idx ? "true" : "false");
        });
        panels.forEach((p, i) => {
          p.hidden = i !== idx;
        });
      };
      tabs.forEach((t, i) => on(t, "click", () => activate(i)));
      activate(0);
    });
  };

  /* ----- Accordion (details/summary is native — we only add analytics hook) ----- */
  const initAccordion = () => {
    // Reserved for future: stagger open, custom animation
  };

  /* ----- Reveal on scroll ----- */
  const initReveal = () => {
    if (!("IntersectionObserver" in window)) return;
    const stagger = document.body.classList.contains("anim-stagger-on");
    if (stagger) {
      document
        .querySelectorAll(
          ".prods-grid, .l1-popular__grid, .l1-catblk__grid, .blog-grid, .related-section__track, .cats-grid",
        )
        .forEach((g) => {
          [...g.children].forEach((child, i) => {
            if (!child.classList.contains("reveal")) child.classList.add("reveal");
            child.style.setProperty("--stagger-i", Math.min(i, 8));
          });
        });
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("is-visible");
            io.unobserve(en.target);
          }
        });
      },
      { rootMargin: "0px 0px -60px 0px" },
    );
    const targets = $$(".reveal");
    if (!targets.length) return;
    targets.forEach((el) => io.observe(el));
    setTimeout(() => {
      targets.forEach((el) => {
        if (!el.classList.contains("is-visible")) el.classList.add("is-visible");
      });
    }, 1500);
  };

  /* ----- Collection page (filters, sort, view toggle, infinite scroll) ----- */
  const initCollectionPage = () => {
    const root = $("[data-collection-section]");
    if (!root) return;

    // #92: atrás/adelante del navegador tras filtrar por AJAX -> recargar la URL real
    if (!T._collPopBound) {
      T._collPopBound = true;
      T._collUrlKey = location.pathname + location.search;
      window.addEventListener("popstate", () => {
        const key = location.pathname + location.search;
        if (key !== T._collUrlKey) {
          T._collUrlKey = key;
          window.location.reload();
        }
      });
    }

    // Mobile filter toggle
    const sidebar = $("#CollSidebar", root);
    const overlay = $(".coll-sidebar-overlay", root);
    $$("[data-coll-filter-toggle]").forEach((btn) =>
      on(btn, "click", (e) => {
        e.preventDefault();
        sidebar?.classList.toggle("is-open");
        overlay?.classList.toggle("is-open");
        document.body.classList.toggle("no-scroll");
      }),
    );

    // View toggle (grid / list)
    const grid = $("[data-coll-grid]");
    $$("[data-coll-view]", root).forEach((btn) =>
      on(btn, "click", () => {
        $$("[data-coll-view]", root).forEach((b) =>
          b.classList.remove("is-active"),
        );
        btn.classList.add("is-active");
        const view = btn.dataset.collView;
        if (grid)
          grid.classList.toggle("coll-prod-grid--list", view === "list");
        try {
          localStorage.setItem("coll_view", view);
        } catch (e) {}
      }),
    );
    try {
      const saved = localStorage.getItem("coll_view");
      if (saved === "list") {
        const btn = $('[data-coll-view="list"]', root);
        if (btn) btn.click();
      }
    } catch (e) {}

    // Sort: AJAX on change
    const sortSel = $("[data-coll-sort]", root);
    if (sortSel)
      on(sortSel, "change", () => {
        const url = new URL(window.location.href);
        url.searchParams.set("sort_by", sortSel.value);
        url.searchParams.delete("page");
        ajaxFilter(url.toString());
      });

    // AJAX filter: filter links + price inputs replace the page without reload
    const filterForm = $("[data-coll-filter-form]");
    let isLoading = false;
    const ajaxFilter = async (url) => {
      if (isLoading) return;
      isLoading = true;
      grid?.classList.add("coll-loading-state");
      try {
        const res = await fetch(url, {
          headers: { "X-Requested-With": "fetch" },
        });
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const newSection = doc.querySelector("[data-collection-section]");
        const oldSection = root;
        if (newSection && oldSection) {
          // Replace inner of old section with new
          oldSection.innerHTML = newSection.innerHTML;
          // Re-init the filter handlers on the new content
          initCollectionPage();
        }
        history.pushState(null, "", url);
        T._collUrlKey = location.pathname + location.search;
        window.scrollTo({
          top: oldSection.offsetTop - 100,
          behavior: "smooth",
        });
      } catch (err) {
        warn("[coll] ajax filter failed", err);
        window.location.href = url;
      } finally {
        isLoading = false;
      }
    };

    // #92: opciones de filtro y etiquetas de filtros activos son <button data-url> (no <a>).
    // Un único listener delegado en el sidebar (se recrea con cada recarga AJAX, no se duplica).
    if (sidebar) {
      onDelegate(sidebar, "click", "[data-url]", (e, btn) => {
        e.preventDefault();
        const url = new URL(btn.dataset.url, window.location.href).toString();
        ajaxFilter(url);
      });
    }

    if (filterForm) {

      // Dual range slider with readout
      const rangeWrap = $(".coll-price-range", filterForm);
      const rangeLo = $(".coll-range-input--lo", filterForm);
      const rangeHi = $(".coll-range-input--hi", filterForm);
      const minDisplay = $("[data-coll-price-min-display]", filterForm);
      const maxDisplay = $("[data-coll-price-max-display]", filterForm);
      const rangeMax = rangeWrap
        ? parseFloat(rangeWrap.dataset.rangeMax || 0)
        : 0;
      const minName = rangeWrap?.dataset.minName;
      const maxName = rangeWrap?.dataset.maxName;
      const cur = T.shop?.currency_symbol || "€";
      const fmt = (n) => `${Math.round(grossCents(n * 100) / 100)} ${cur}`;

      const submitPrice = () => {
        const url = new URL(window.location.href);
        const lo = parseFloat(rangeLo?.value || 0);
        const hi = parseFloat(rangeHi?.value || rangeMax);
        if (minName) {
          if (lo > 0) url.searchParams.set(minName, lo * 100);
          else url.searchParams.delete(minName);
        }
        if (maxName) {
          if (hi > 0 && hi < rangeMax) url.searchParams.set(maxName, hi * 100);
          else url.searchParams.delete(maxName);
        }
        url.searchParams.delete("page");
        ajaxFilter(url.toString());
      };

      const updateVisual = (e) => {
        if (!rangeWrap || !rangeLo || !rangeHi || rangeMax <= 0) return;
        let lo = parseFloat(rangeLo.value);
        let hi = parseFloat(rangeHi.value);
        if (lo > hi - 1) {
          if (e && e.target === rangeLo) lo = hi - 1;
          else hi = lo + 1;
          rangeLo.value = lo;
          rangeHi.value = hi;
        }
        rangeWrap.style.setProperty("--range-lo", (lo / rangeMax) * 100 + "%");
        rangeWrap.style.setProperty("--range-hi", (hi / rangeMax) * 100 + "%");
        if (minDisplay) minDisplay.textContent = fmt(lo);
        if (maxDisplay) maxDisplay.textContent = fmt(hi);
      };

      let priceTimer;
      const submitPriceDebounced = () => {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(submitPrice, 600);
      };

      [rangeLo, rangeHi].forEach((r) => {
        if (!r) return;
        on(r, "input", updateVisual);
        on(r, "change", submitPriceDebounced);
      });

      // Numeric range filters (metafield-based: height, capacity, etc.)
      $$(".coll-numeric-range", filterForm).forEach((wrap) => {
        const lo = wrap.querySelector(".coll-range-input--lo");
        const hi = wrap.querySelector(".coll-range-input--hi");
        const minOut = wrap.parentElement.querySelector("[data-coll-numeric-min-display]");
        const maxOut = wrap.parentElement.querySelector("[data-coll-numeric-max-display]");
        const max = parseFloat(wrap.dataset.rangeMax || 0);
        const mn = wrap.dataset.minName;
        const mx = wrap.dataset.maxName;
        const unit = wrap.dataset.unit || "";
        const fmt = (n) => unit ? `${Math.round(n)} ${unit}` : `${Math.round(n)}`;
        if (!lo || !hi || max <= 0) return;

        const paint = (e) => {
          let l = parseFloat(lo.value);
          let h = parseFloat(hi.value);
          if (l > h - 1) {
            if (e && e.target === lo) l = hi - 1;
            else hi = l + 1;
            lo.value = l;
            hi.value = h;
          }
          wrap.style.setProperty("--range-lo", (l / max) * 100 + "%");
          wrap.style.setProperty("--range-hi", (h / max) * 100 + "%");
          if (minOut) minOut.textContent = fmt(l);
          if (maxOut) maxOut.textContent = fmt(h);
        };
        const submit = () => {
          const url = new URL(window.location.href);
          const l = parseFloat(lo.value);
          const h = parseFloat(hi.value);
          if (mn) (l > 0 ? url.searchParams.set(mn, l) : url.searchParams.delete(mn));
          if (mx) (h > 0 && h < max ? url.searchParams.set(mx, h) : url.searchParams.delete(mx));
          url.searchParams.delete("page");
          ajaxFilter(url.toString());
        };
        let t;
        const debounced = () => { clearTimeout(t); t = setTimeout(submit, 600); };
        on(lo, "input", paint); on(hi, "input", paint);
        on(lo, "change", debounced); on(hi, "change", debounced);
      });

      // Active filter chips: los gestiona el listener [data-url] del sidebar (#92)
    }

    const mode = root.dataset.paginationMode || "load_more";
    const infinite = mode === "infinite";
    if (mode === "paginate") return;
    const loadMoreBtn = $("[data-coll-load-more]");
    if (!loadMoreBtn) return;

    const fetchPage = async (url) => {
      try {
        loadMoreBtn.innerHTML = '<span class="coll-loading"></span>';
        loadMoreBtn.setAttribute("disabled", "true");
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const newGrid = doc.querySelector("[data-coll-grid]");
        const newWrap = doc.querySelector(".coll-load-more-wrap");
        if (newGrid && grid) {
          newGrid
            .querySelectorAll(":scope > *")
            .forEach((el) => grid.appendChild(el));
        }
        const oldWrap = loadMoreBtn.closest(".coll-load-more-wrap");
        if (oldWrap && newWrap) oldWrap.replaceWith(newWrap);
        else if (oldWrap) oldWrap.remove();
        if (infinite) initInfinite();
      } catch (err) {
        warn("[coll] load more failed", err);
        loadMoreBtn.removeAttribute("disabled");
        loadMoreBtn.textContent = "Mehr laden";
      }
    };

    const onClick = (e) => {
      e.preventDefault();
      const btn = $("[data-coll-load-more]");
      if (btn && !btn.hasAttribute("disabled")) fetchPage(btn.href);
    };
    on(loadMoreBtn, "click", onClick);

    const initInfinite = () => {
      const btn = $("[data-coll-load-more]");
      if (!btn || !infinite || !("IntersectionObserver" in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting && !btn.hasAttribute("disabled")) {
              io.disconnect();
              fetchPage(btn.href);
            }
          });
        },
        { rootMargin: "300px" },
      );
      io.observe(btn);
      on(loadMoreBtn, "click", onClick);
    };
    if (infinite) initInfinite();
  };

  /* ----- PDP lightbox ----- */
  const initPdpLightbox = () => {
    const main = $("[data-gallery-main]");
    const lightbox = $("#PdpLightbox");
    if (!main || !lightbox) return;
    const img = $(".pdp-lightbox__img", lightbox);
    const counter = $(".pdp-lightbox__counter", lightbox);
    const thumbs = $$("[data-gallery-thumb]");
    const sources = thumbs.length
      ? thumbs.map((t) => t.dataset.src)
      : [main.querySelector("img")?.src].filter(Boolean);
    let idx = 0;

    const updateCounter = () => {
      if (counter) counter.textContent = `${idx + 1} / ${sources.length}`;
    };
    const show = (i) => {
      idx = (i + sources.length) % sources.length;
      img.src = sources[idx];
      updateCounter();
    };
    const open = () => {
      const currentSrc = main.querySelector("img")?.getAttribute("src");
      const matchIdx = sources.findIndex((s) => s === currentSrc);
      idx = matchIdx >= 0 ? matchIdx : 0;
      show(idx);
      lightbox.classList.add("is-open");
      document.body.classList.add("no-scroll");
    };
    const close = () => {
      lightbox.classList.remove("is-open");
      document.body.classList.remove("no-scroll");
    };

    on(main, "click", open);
    on($(".pdp-lightbox__close", lightbox), "click", close);
    on(lightbox, "click", (e) => {
      if (e.target === lightbox) close();
    });
    on($(".pdp-lightbox__nav--prev", lightbox), "click", () => show(idx - 1));
    on($(".pdp-lightbox__nav--next", lightbox), "click", () => show(idx + 1));
    on(document, "keydown", (e) => {
      if (!lightbox.classList.contains("is-open")) return;
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") show(idx - 1);
      else if (e.key === "ArrowRight") show(idx + 1);
    });
  };

  /* ----- Trust-bar mobile auto-rotate ----- */
  /* ----- Predictive search ----- */
  const initPredictiveSearch = () => {
    const form = $('[data-predictive-search="true"]');
    if (!form) return;
    const input = $("input[name=q]", form);
    const out = $("[data-predictive-results]", form);
    if (!input || !out) return;
    let timer;
    const close = () => {
      out.classList.remove("is-open");
      out.setAttribute("aria-hidden", "true");
      out.innerHTML = "";
    };
    const fetchResults = async (q) => {
      try {
        const url = `${T.routes.predictive_search}?q=${encodeURIComponent(
          q,
        )}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        if (!res.ok) {
          close();
          return;
        }
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const part = doc.querySelector("[data-predictive-output]");
        if (part && part.innerHTML.trim()) {
          out.innerHTML = part.innerHTML;
          applyVatMode(out);
          out.classList.add("is-open");
          out.setAttribute("aria-hidden", "false");
        } else {
          close();
        }
      } catch (e) {
        warn("[predictive]", e);
        close();
      }
    };
    on(input, "input", () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (q.length < 2) return close();
      timer = setTimeout(() => fetchResults(q), 200);
    });
    on(input, "blur", () => setTimeout(close, 200));
    on(input, "focus", () => {
      if (input.value.trim().length >= 2 && out.innerHTML.trim()) {
        out.classList.add("is-open");
        out.setAttribute("aria-hidden", "false");
      }
    });
  };

  /* ----- Sticky header host ----- */
  const initStickyHeader = () => {
    const header = $(".site-header--sticky");
    if (!header) return;
    const wrapper = header.closest(".shopify-section");
    if (wrapper) wrapper.classList.add("is-sticky-host");
  };

  const initTrustBarRotate = () => {
    $$(".trust-bar--mobile-carousel[data-trust-rotate]").forEach((root) => {
      const interval = parseInt(root.dataset.trustRotate || "0", 10) * 1000;
      if (!interval) return;
      const items = $$(".trust-item", root);
      if (items.length <= 1) return;
      let idx = 0;
      const tick = () => {
        if (window.innerWidth > 700) return;
        idx = (idx + 1) % items.length;
        const target =
          items[idx].getBoundingClientRect().left -
          root.getBoundingClientRect().left +
          root.scrollLeft;
        root.scrollTo({ left: target, behavior: "smooth" });
      };
      setInterval(tick, interval);
    });
  };

  /* ----- Mobile search toggle ----- */
  const initMobileSearch = () => {
    const trigger = $("[data-mobile-search-trigger]");
    const wrap = $(".search-wrap");
    if (!trigger || !wrap) return;
    on(trigger, "click", (e) => {
      e.preventDefault();
      wrap.classList.toggle("is-mobile-open");
      const input = $("input", wrap);
      if (wrap.classList.contains("is-mobile-open") && input)
        setTimeout(() => input.focus(), 100);
    });
  };

  /* ----- Hero slider ----- */
  const initHeroSlider = () => {
    $$(".hero-slider").forEach((root) => {
      const slides = $$(".hero-slider__slide", root);
      if (slides.length <= 1) return;
      const interval = parseInt(root.dataset.interval || "5", 10) * 1000;
      const dots = $$(".hero-slider__dot", root);
      let idx = 0,
        timer = null;
      const show = (i) => {
        slides.forEach((s, n) => s.classList.toggle("is-active", n === i));
        dots.forEach((d, n) => d.classList.toggle("is-active", n === i));
      };
      const tick = () => {
        idx = (idx + 1) % slides.length;
        show(idx);
      };
      const start = () => {
        if (interval > 0) timer = setInterval(tick, interval);
      };
      const stop = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };
      show(0);
      start();
      dots.forEach((d, i) =>
        on(d, "click", () => {
          stop();
          idx = i;
          show(idx);
          start();
        }),
      );
      on(root, "mouseenter", stop);
      on(root, "mouseleave", start);
    });
  };

  /* ----- Announcement bar mobile carousel ----- */
  const initAnnouncementRotator = () => {
    const tb = $("[data-topbar]");
    if (!tb) return;
    const items = $$(".tb-left .tb-item", tb);
    if (items.length <= 1) return;
    const interval = parseInt(tb.dataset.rotateInterval || "0", 10) * 1000;
    if (interval <= 0) return;
    const mql = window.matchMedia("(max-width:767px)");
    let idx = 0,
      timer = null;
    const show = (i) => {
      items.forEach((el, n) => el.classList.toggle("is-visible", n === i));
    };
    const start = () => {
      if (!mql.matches) {
        items.forEach((el) => el.classList.remove("is-visible"));
        return;
      }
      show(0);
      idx = 0;
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        idx = (idx + 1) % items.length;
        show(idx);
      }, interval);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    start();
    (mql.addEventListener
      ? mql.addEventListener.bind(mql, "change")
      : mql.addListener.bind(mql))((e) => {
      stop();
      start();
    });
  };

  /* ----- Product carousel nav ----- */
  const initProductCarousel = () => {
    $$(".bests-section").forEach((root) => {
      const track = $(".product-carousel__track", root);
      if (!track) return;
      const prev = $("[data-carousel-prev]", root);
      const next = $("[data-carousel-next]", root);
      const step = () => {
        const child = track.firstElementChild;
        if (!child) return track.clientWidth * 0.75;
        const gap = parseInt(getComputedStyle(track).columnGap || getComputedStyle(track).gap, 10) || 0;
        return child.clientWidth + gap;
      };
      if (prev) on(prev, "click", () => track.scrollBy({ left: -step(), behavior: "smooth" }));
      if (next) on(next, "click", () => track.scrollBy({ left: step(), behavior: "smooth" }));
    });
  };

  /* ----- Header cart count from Liquid-rendered markup ----- */
  const initHeader = () => {
    // Initial count update from bootstrap (server-rendered is authoritative)
    // No-op by default
  };

  /* ----- Collapsible FAQ inside SEO text (.collapsible-container) ----- */
  const initCollapsibleFaq = () => {
    const items = $$(".collapsible-item");
    if (!items.length) return;
    items.forEach((item) => {
      const h2 = item.querySelector(":scope > h2");
      const panel = item.querySelector(":scope > div");
      const open = item.classList.contains("active");
      if (h2) {
        h2.setAttribute("aria-expanded", open ? "true" : "false");
        if (!h2.hasAttribute("role")) h2.setAttribute("role", "button");
        if (!h2.hasAttribute("tabindex")) h2.setAttribute("tabindex", "0");
      }
      if (panel) panel.setAttribute("aria-hidden", open ? "false" : "true");
    });
    const toggle = (h2) => {
      const item = h2.closest(".collapsible-item");
      if (!item) return;
      const open = item.classList.toggle("active");
      h2.setAttribute("aria-expanded", open ? "true" : "false");
      const panel = item.querySelector(":scope > div");
      if (panel) panel.setAttribute("aria-hidden", open ? "false" : "true");
    };
    onDelegate(document, "click", ".collapsible-item > h2", (e, h2) => toggle(h2));
    onDelegate(document, "keydown", ".collapsible-item > h2", (e, h2) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        toggle(h2);
      }
    });
  };

  /* ----- Init everything ----- */
  const init = () => {
    initNavDrawer();
    initSearchDrawer();
    initCartDrawer();
    initCartDrawerEvents();
    initQuickAdd();
    initProductForms();
    initQty();
    initGallery();
    initThumbsCarousel();
    initOptionPickers();
    initTabs();
    initAccordion();
    initReveal();
    initProductCarousel();
    initAnnouncementRotator();
    initHeroSlider();
    initCollectionPage();
    initPdpLightbox();
    initPdpStickyAtc();
    initMobileSearch();
    initTrustBarRotate();
    initStickyHeader();
    initPredictiveSearch();
    initHeader();
    initCollapsibleFaq();
    // Prime cart on load (for header count + drawer state)
    fetchCart();
    // VAT display: server rendered prices WITH VAT (mode "incl"). If the visitor chose
    // "sin IVA" before, swap static prices to the stored net. Wire the toggle buttons.
    initVatToggle();
    initMarketVatSwitchLabels();
  };

  if (document.readyState === "loading") on(document, "DOMContentLoaded", init);
  else init();
})();
